import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { IMAGES } from "../data/content";
import SectionHeading from "../components/SectionHeading";
import useReveal from "../hooks/useReveal";

const certifications = [
    "Certified Clinical Hypnotherapist",
    "Trained Family Constellation Facilitator",
    "Conscious Connected Breathwork Practitioner",
    "Sound Healing Practitioner",
    "Inner Child & Trauma-Informed Coach",
    "Tarot & Soul Contract Reader",
];

const philosophy = [
    {
        title: "Integrative",
        body: "Modern psychology, somatic awareness, and soul-rooted modalities — woven together, never siloed.",
    },
    {
        title: "Trauma-informed",
        body: "Pace is led by your nervous system, not by a method. Safety is the first medicine.",
    },
    {
        title: "Devotional",
        body: "Healing is held as sacred work. Slow, reverent, and never extractive.",
    },
];

export default function AboutPage() {
    useReveal();
    return (
        <div className="pt-24" data-testid="about-page">
            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                    <div className="lg:col-span-5 ssh-reveal">
                        <div className="aspect-[4/5] overflow-hidden bg-[#2C2B29]">
                            <img
                                src={IMAGES.shreya}
                                alt="Shreya — Clinical Hypnotherapist & Psycho-Spiritual Coach"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                    <div className="lg:col-span-7 ssh-reveal">
                        <p className="ssh-eyebrow">Meet Shreya</p>
                        <h1 className="mt-5 font-serif font-light tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-[#2C2B29]">
                            Holding space where{" "}
                            <em className="italic text-[#A68A6B]">
                                psychology meets soul.
                            </em>
                        </h1>
                        <p className="mt-8 text-lg leading-relaxed text-[#4A4947]">
                            Shreya is a Clinical Hypnotherapist and
                            Psycho-Spiritual Coach. Her practice is a quiet
                            weaving of modern psychology, subconscious healing,
                            emotional wellness, and intuitive spiritual
                            guidance.
                        </p>
                        <p className="mt-4 text-lg leading-relaxed text-[#4A4947]">
                            She works with women and individuals navigating
                            emotional pain, identity rebuilding, ancestral
                            patterns, nervous system overwhelm, and the long,
                            tender path of returning home to oneself.
                        </p>
                        <p className="mt-4 text-lg leading-relaxed text-[#4A4947]">
                            Her sessions are grounded, slow, and held with deep
                            respect — drawing from hypnotherapy, breathwork,
                            family constellation, sound healing, inner child
                            work, and soul guidance.
                        </p>
                    </div>
                </div>
            </section>

            <section className="bg-[#EFEBE4]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
                    <div className="ssh-reveal">
                        <SectionHeading
                            eyebrow="Healing Philosophy"
                            title="Three quiet principles"
                            titleItalic="that guide every session."
                        />
                    </div>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-10">
                        {philosophy.map((p, i) => (
                            <div
                                key={p.title}
                                className="ssh-reveal"
                                style={{ transitionDelay: `${0.07 * i}s` }}
                            >
                                <span className="ssh-divider" />
                                <h3 className="mt-5 font-serif text-3xl text-[#2C2B29]">
                                    {p.title}
                                </h3>
                                <p className="mt-4 text-base text-[#4A4947] leading-relaxed">
                                    {p.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5 ssh-reveal">
                        <SectionHeading
                            eyebrow="Trainings & Certifications"
                            title="Trained, devoted,"
                            titleItalic="and continually learning."
                        />
                    </div>
                    <div className="lg:col-span-7 ssh-reveal">
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                            {certifications.map((c) => (
                                <li
                                    key={c}
                                    className="flex gap-3 text-base text-[#2C2B29] py-3 border-b border-[#EFEBE4]"
                                >
                                    <span className="text-[#A68A6B]">—</span>
                                    <span>{c}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <section className="bg-[#FAF8F5] border-t border-[#EFEBE4]">
                <div className="max-w-5xl mx-auto px-6 lg:px-12 py-20 lg:py-28 text-center ssh-reveal">
                    <h2 className="font-serif font-light tracking-tight leading-[1.05] text-4xl sm:text-5xl text-[#2C2B29]">
                        Ready to begin?{" "}
                        <em className="italic text-[#A68A6B]">
                            Let's meet softly.
                        </em>
                    </h2>
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                        <Link to="/contact#booking">
                            <Button
                                className="ssh-btn-primary rounded-none px-9 py-7 text-xs uppercase tracking-[0.22em]"
                                data-testid="about-book-session"
                            >
                                Book a Session
                            </Button>
                        </Link>
                        <Link to="/services">
                            <Button
                                variant="outline"
                                className="rounded-none border-[#2C2B29] bg-transparent text-[#2C2B29] hover:bg-[#2C2B29] hover:text-white px-9 py-7 text-xs uppercase tracking-[0.22em] transition-colors"
                            >
                                Explore Services
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
